<template>
  <v-container
    fluid
    fill-height
    class="pa-0"
  >
    <v-slide-y-transition mode="out-in">
      <v-layout
        v-if="checkpoint"
        v-bind="binding"
        style="position: relative"
      >
        <v-flex
          order-xs2
          order-md1
          order-lg1
          order-xl1
          md4
        >
          <v-card class="full-height">
            <image-with-fullscreen-dialog
              v-if="checkpoint.image_card"
              :fullscreen-image="checkpoint.image.meta.download_url"
              :preview-image="checkpoint.image_card.url"
              :title="checkpoint.name"
              :aspect-ratio="2"
            />
            <v-card-actions>
              <v-btn
                v-if="previousCheckpoint"
                :disabled="!previousCheckpoint.id"
                :to="{name: 'checkpoint', params: {strollId, checkpointId: previousCheckpoint.id}}"
                color="indigo"
                exact
                flat
              >
                <v-icon>chevron_left</v-icon>
                Prev
              </v-btn>
              <v-spacer />
              <v-btn
                v-if="nextCheckpoint"
                :disabled="!nextCheckpoint.id"
                :to="{name: 'checkpoint', params: {strollId, checkpointId: nextCheckpoint.id}}"
                color="indigo"
                exact
                flat
              >
                Next
                <v-icon>chevron_right</v-icon>
              </v-btn>
            </v-card-actions>
            <v-card-title>
              <v-icon left>place</v-icon>
              <span class="title font-weight-light" v-text="checkpoint.name"/>
            </v-card-title>
            <v-card-text>
              <div v-if="!stroll.in_person_enabled">
                <div v-html="checkpoint.body" />
                <div v-if="checkpoint.media">
                  <h3 class="headline mb-2">Media</h3>
                  <media :media="checkpoint.media" />
                </div>
              </div>
              <stroll-in-person-message v-if="stroll.in_person_enabled" :stroll="stroll">
                <div v-html="checkpoint.body" />
                <div v-if="checkpoint.media">
                  <h3 class="headline mb-2">Media</h3>
                  <media :media="checkpoint.media" />
                </div>
              </stroll-in-person-message>
            </v-card-text>
            <v-card-actions>
              <v-btn
                :to="{name: 'stroll', params: {strollId}}"
                exact
                flat
              >
                <v-icon left>arrow_back</v-icon>
                Back to {{ strollWording }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
        <v-flex
          md-8
          order-xs1
          order-sm1
          order-md2
        >
          <l-map
            :center="center"
            :options="mapOptions"
            :zoom="zoom"
          >
            <l-tile-layer
              :url="url"
              :attribution="attribution"
              :options="{useCache: true}"
            />
            <l-circle-marker
              v-if="location"
              :lat-lng="[location.latitude, location.longitude]"
              :radius="6"
              :fill="true"
              :fill-opacity="0.3"
            />
            <l-marker
              :title="checkpoint.name"
              :lat-lng="checkpoint.point.coordinates"
            />
            <l-marker
              v-if="nextCheckpoint"
              :title="nextCheckpoint.name"
              :lat-lng="nextCheckpoint.point.coordinates"
            >
              <l-icon
                icon-url="https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png"
                shadow-url="https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png"
              />
            </l-marker>
            <l-marker
              v-if="previousCheckpoint"
              :title="previousCheckpoint.name"
              :lat-lng="previousCheckpoint.point.coordinates"
            >
              <l-icon
                icon-url="https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png"
                shadow-url="https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png"
              />
            </l-marker>
            <l-control-zoom :position="zoomPosition" />
          </l-map>
        </v-flex>
      </v-layout>
    </v-slide-y-transition>
  </v-container>
</template>

<script>
import L from 'leaflet'
import { LCircleMarker, LControlZoom, LIcon, LMap, LMarker, LTileLayer } from 'vue2-leaflet'
import { mapActions, mapGetters } from 'vuex'
import 'leaflet.tilelayer.pouchdbcached'
import ImageWithFullscreenDialog from '@/components/ImageWithFullscreenDialog.vue'
import PouchDB from 'pouchdb'
import StrollInPersonMessage from '@/components/StrollInPersonMessage.vue'
import Media from '@/components/Media.vue'
import { leafletDefaults } from '@/constants/leaflet'

export default {
  name: 'Checkpoint',

  components: {
    ImageWithFullscreenDialog,
    LCircleMarker,
    LControlZoom,
    LIcon,
    LMap,
    LMarker,
    LTileLayer,
    Media,
    StrollInPersonMessage,
  },

  props: {
    checkpointId: {
      type: [Number, String],
      default: null,
    },
    strollId: {
      type: [Number, String],
      default: null,
    },
  },

  data () {
    return {
      ...leafletDefaults,
      checkpoint: null,
      stroll: null,
      zoom: 16,
      center: L.latLng(45.3157247, -64.4338494),
      mapOptions: { zoomControl: false },
    }
  },

  computed: {
    ...mapGetters({
      location: 'getLocation',
      strollWording: 'getStrollWording',
    }),

    binding () {
      const binding = {column: true}

      if (this.$vuetify.breakpoint.mdAndUp) {
        binding.column = false
      }

      return binding
    },

    checkpointCount() {
      if (!this.stroll) {
        return 0
      }
      return this.stroll.checkpoints.length
    },

    checkpointIndex() {
      return this.stroll.checkpoints.findIndex((checkpoint) => checkpoint.id == this.checkpointId)
    },

    nextCheckpoint() {
      const lastCheckpointIndex = this.checkpointCount - 1
      if (this.checkpointIndex == lastCheckpointIndex) {
        return null
      }
      const nextCheckpointIndex = this.checkpointIndex + 1
      const nextCheckpoint = this.stroll.checkpoints[nextCheckpointIndex]
      return nextCheckpoint
    },

    previousCheckpoint() {
      if (this.checkpointIndex == 0) {
        return null
      }
      const previousCheckpointIndex = this.checkpointIndex - 1
      const previousCheckpoint = this.stroll.checkpoints[previousCheckpointIndex]
      return previousCheckpoint
    },
  },

  watch: {
    // call again the method if the route changes
    '$route': 'fetchCheckpoint'
  },

  async created() {
    if (window !== undefined) {
      window.PouchDB = PouchDB
    }
    if (!this.location.latitude || !this.location.longitude) {
      await this.fetchLocation()
      await this.getCurrentPosition()
    }

    this.stroll = await this.fetchStroll({id: this.strollId})
    this.fetchCheckpoint()
  },

  methods: {
    ...mapActions([
      'getCurrentPosition',
      'fetchLocation',
      'fetchStroll',
    ]),

    fetchCheckpoint() {
      this.checkpoint = this.stroll.checkpoints.find(checkpoint => checkpoint.id == this.checkpointId)
      this.center = this.checkpoint.point.coordinates
    },

  },
}
</script>

<style scoped>
.vue2leaflet-map.leaflet-container {
  min-height: 250px;
  height: 100%;
}
</style>
